/**
 * Kulüp üye rehberi — örnek kayıt seti (yaklaşık 200 kişi).
 * Üretimde API veya CMS ile değiştirin.
 *
 * @typedef {{
 *   id: string,
 *   givenName: string,
 *   familyName: string,
 *   partnerGivenName?: string,
 *   partnerFamilyName?: string,
 *   partnerId?: string,
 *   birthDate: string,
 *   joinedAt: string,
 *   profession: string,
 *   email: string,
 *   company?: string,
 *   sector?: string,
 *   profilePictureUrl?: string,
 *   partnerProfilePictureUrl?: string,
 *   groupLeader?: boolean,
 * }} ClubMember
 */

/** Türkçe alfabe — soyadı ilk harfine göre filtre */
export const MEMBER_ALPHABET_LETTERS = [
    'A',
    'B',
    'C',
    'Ç',
    'D',
    'E',
    'F',
    'G',
    'Ğ',
    'H',
    'I',
    'İ',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'Ö',
    'P',
    'R',
    'S',
    'Ş',
    'T',
    'U',
    'Ü',
    'V',
    'Y',
    'Z',
];

/** @type {Record<string, string[]>} */
const SURNAME_BY_LETTER = {
    A: ['Arslan', 'Aydın', 'Aksoy', 'Acar', 'Aslan'],
    B: ['Bulut', 'Bozkurt', 'Barut', 'Başar'],
    C: ['Can', 'Ceylan', 'Coşkun'],
    Ç: ['Çelik', 'Çetin', 'Çınar', 'Çolak'],
    D: ['Demir', 'Doğan', 'Duran', 'Dönmez'],
    E: ['Erdem', 'Ertürk', 'Erdoğan', 'Ekinci'],
    F: ['Fırat', 'Filiz'],
    G: ['Güneş', 'Güler', 'Gök', 'Genç'],
    Ğ: ['Güven'],
    H: ['Hacıoğlu', 'Horuz', 'Halıcı'],
    I: ['Işık', 'Ilıcak'],
    İ: ['İpek', 'İnan', 'İşler'],
    J: ['Jülide', 'Jale'],
    K: ['Kaya', 'Kurt', 'Koç', 'Kara', 'Kılıç'],
    L: ['Lale'],
    M: ['Mutlu', 'Mertoğlu', 'Mercan'],
    N: ['Nalbant', 'Nazlı'],
    O: ['Oral', 'Oktay'],
    Ö: ['Öztürk', 'Özdemir', 'Özkan', 'Önal'],
    P: ['Polat', 'Pekkan'],
    R: ['Rençber'],
    S: ['Şahin', 'Soylu', 'Sezer'],
    Ş: ['Şimşek', 'Şen', 'Şeker'],
    T: ['Tunç', 'Tekin', 'Türkoğlu'],
    U: ['Uçar', 'Uslu'],
    Ü: ['Ünal', 'Üstün'],
    V: ['Varol'],
    Y: ['Yalçın', 'Yavuz', 'Yüksel'],
    Z: ['Zengin', 'Zorlu'],
};

const GIVEN_MALE = [
    'Ahmet',
    'Mehmet',
    'Mustafa',
    'Ali',
    'Hüseyin',
    'Hasan',
    'İbrahim',
    'Murat',
    'Osman',
    'Emre',
    'Burak',
    'Cem',
    'Deniz',
    'Eren',
    'Kaan',
];

const GIVEN_FEMALE = [
    'Ayşe',
    'Fatma',
    'Zeynep',
    'Elif',
    'Merve',
    'Selin',
    'Gizem',
    'Pınar',
    'Esra',
    'Derya',
    'Burcu',
    'Cansu',
    'Hande',
    'İpek',
    'Özge',
];

const PARTNER_GIVEN_MALE = GIVEN_MALE;
const PARTNER_GIVEN_FEMALE = GIVEN_FEMALE;

const PROFESSIONS = [
    'Avukat',
    'Mimar',
    'Mühendis',
    'İşletmeci',
    'Doktor',
    'Eczacı',
    'Mali müşavir',
    'Yönetici danışmanı',
    'Serbest meslek',
    'Finans uzmanı',
    'Öğretim üyesi',
    'Girişimci',
    'İnsan kaynakları',
    'Pazarlama direktörü',
    'Yazılım uzmanı',
];

const MEMBER_COUNT = 200;

/**
 * Soyadının filtre harfi (Türkçe büyük harf).
 * @param {string} familyName
 */
export function getMemberSurnameLetter(familyName) {
    const t = familyName.trim();
    if (!t) return 'A';
    return t.charAt(0).toLocaleUpperCase('tr-TR');
}

/**
 * Adın (isim) ilk harfi — alfabe filtresi için.
 * @param {string} givenName
 */
export function getMemberGivenNameLetter(givenName) {
    const t = givenName.trim();
    if (!t) return 'A';
    return t.charAt(0).toLocaleUpperCase('tr-TR');
}

/**
 * Üye rehberi sıralaması — ada göre, eşitse soyada göre (tr-TR).
 * @param {ClubMember} a
 * @param {ClubMember} b
 */
export function compareClubMembersByGivenName(a, b) {
    const byGiven = a.givenName.localeCompare(b.givenName, 'tr-TR', {
        sensitivity: 'base',
    });
    if (byGiven !== 0) return byGiven;
    return a.familyName.localeCompare(b.familyName, 'tr-TR', {
        sensitivity: 'base',
    });
}

/**
 * @param {ClubMember[]} rows
 * @returns {ClubMember[]}
 */
export function sortClubMembersByGivenName(rows) {
    return [...rows].sort(compareClubMembersByGivenName);
}

/**
 * Arama / filtre için metin: küçük harf (tr) + Türkçe harfleri ASCII eşdeğerine indirger,
 * böylece "Oğuz" ile "Oguz" aynı anahtara düşer.
 * @param {string} s
 */
export function normalizeMemberSearchText(s) {
    const t = String(s).trim().toLocaleLowerCase('tr-TR');
    return t
        .replace(/ç/g, 'c')
        .replace(/ğ/g, 'g')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ş/g, 's')
        .replace(/ü/g, 'u');
}

function pick(arr, i) {
    return arr[i % arr.length];
}

function randomDateBetween(startYear, endYear, seed) {
    const span = endYear - startYear + 1;
    const y =
        startYear +
        (Math.floor(Math.abs(Math.sin(seed) * 10000)) % span);
    const m =
        1 + (Math.floor(Math.abs(Math.cos(seed * 1.7) * 100)) % 12);
    const d =
        1 + (Math.floor(Math.abs(Math.sin(seed * 2.3) * 100)) % 28);
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function buildMembers() {
    /** @type {ClubMember[]} */
    const list = [];
    const letterCount = MEMBER_ALPHABET_LETTERS.length;

    for (let i = 0; i < MEMBER_COUNT; i++) {
        const letter = MEMBER_ALPHABET_LETTERS[i % letterCount];
        const pool = SURNAME_BY_LETTER[letter] || ['Yılmaz'];
        const familyName = pool[Math.floor(i / letterCount) % pool.length];
        const isMale = i % 2 === 0;
        const givenName = isMale
            ? pick(GIVEN_MALE, i)
            : pick(GIVEN_FEMALE, i);
        const hasPartner = i % 3 !== 0;
        let partnerGivenName;
        let partnerFamilyName;
        /** @type {string | undefined} */
        let partnerId;
        if (hasPartner) {
            partnerGivenName = isMale
                ? pick(PARTNER_GIVEN_FEMALE, i + 7)
                : pick(PARTNER_GIVEN_MALE, i + 7);
            partnerFamilyName = pick(pool, i + 3);
            partnerId = `m-${String(i + 1).padStart(4, '0')}-partner`;
        }

        const birthDate = randomDateBetween(1955, 1992, i + 0.1);
        const joinedAt = randomDateBetween(1998, 2024, i + 0.5);
        const profession = pick(PROFESSIONS, i);
        const email = `uye.${String(i + 1).padStart(4, '0')}@istanbulrotary.org`;

        list.push({
            id: `m-${String(i + 1).padStart(4, '0')}`,
            givenName,
            familyName,
            partnerGivenName,
            partnerFamilyName,
            partnerId,
            birthDate,
            joinedAt,
            profession,
            email,
        });
    }

    return sortClubMembersByGivenName(list);
}

/** @type {ClubMember[]} */
export const MEMBERS = buildMembers();

/** @type {Map<string, ClubMember>} */
export const MEMBER_BY_ID = new Map(MEMBERS.map((m) => [m.id, m]));

/**
 * Verilen sırayla üyeleri döndürür; bulunamayan id’ler atlanır.
 * @param {string[]} ids
 * @returns {ClubMember[]}
 */
export function getMembersByIds(ids) {
    return ids.map((id) => MEMBER_BY_ID.get(id)).filter(Boolean);
}

/**
 * Simülasyon detay URL’si — ana üye id veya `partnerId`.
 * @param {string} memberId
 * @returns {{ record: ClubMember, viewAsPartner: boolean } | null}
 */
export function findClubMemberForDetailId(memberId) {
    const direct = MEMBER_BY_ID.get(memberId);
    if (direct) {
        return { record: direct, viewAsPartner: false };
    }
    for (const m of MEMBERS) {
        if (m.partnerId && m.partnerId === memberId) {
            return { record: m, viewAsPartner: true };
        }
    }
    return null;
}
